import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { getBatteryIcon } from "./getBatteryIcon"
import * as Icons from "@nova/component-icons"
import { useUserLocation } from "features/user-locations/user-location-store"
import classNames from "classnames"
import {
    LayerListItemActions,
    SidePanelListItemAction,
} from "panels/content/side-panel/generics/GenericLayerListItem"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { TimeRemainingPie } from "features/overview/sharing-location/TimeRemainingPie"
import { useTimeRemaining } from "@nova/toolkit-pure-hooks"
import { PluginSpec } from "plugins/plugin-registry"
import { LiveBadge } from "features/overview/search/searchResults/LiveBadge"
import {
    selectStarredAtlasLiveVideoFlights,
    starAtlasLiveVideoFlight,
    unstarAtlasLiveVideoFlight,
} from "features/entity-caching/flights/slice"

export const getUseVisibilityActions = (spec: PluginSpec) => {
    const useVisibilityAction = (id: string) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const { isEditor } = useWorkspacePermissions()
        return [
            {
                icon: Icons.ZoomTo,
                onClick: () => {
                    spec.zoomTo(entity)
                },
                className: "cursor-pointer",
            },
            {
                icon: entity.visible ? Icons.VisibilityEye : Icons.VisibilityEyeOff,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(
                        spec.updateAction({
                            id,
                            updates: { visible: !entity.visible },
                        })
                    )
                },
                keepVisible: true,
                className: "cursor-pointer",
            },
        ]
    }
    return useVisibilityAction
}

export const useLiveAtlasLiveVideoActions = (id: string) => {
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const starredAtlasLiveVideoIds = useAppSelector(selectStarredAtlasLiveVideoFlights)
    const isStarred = starredAtlasLiveVideoIds.includes(id)
    return [
        {
            icon: isStarred ? Icons.StarFill : Icons.Star,
            onClick: () => {
                if (!isEditor) return
                if (isStarred) {
                    dispatch(unstarAtlasLiveVideoFlight(id))
                } else {
                    dispatch(starAtlasLiveVideoFlight(id))
                }
            },
            keepVisible: isStarred,
            className: "cursor-pointer",
        },
    ]
}

export const getGenericFlyoutLayerListItem = ({
    showLiveBadge,
    useActions,
    spec,
}: {
    showLiveBadge: boolean
    useActions: (id: string) => SidePanelListItemAction[]
    spec: PluginSpec
}) => {
    const Component = ({ id, isHovered }: { id: string; isHovered: boolean }) => {
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const userId = entity.createdBy?.userId ?? ""
        const location = useUserLocation(userId)
        const timeRemaining = useTimeRemaining({ timestamp: location?.expiresAt })
        const members = useAppSelector(selectWorkspaceMembers)
        const member = members.find((m) => m.userId === userId)
        if (!member || !entity) return null
        return (
            <div className="flex items-center gap-3 w-full hover:bg-gray-700 py-1 px-2 rounded-xl">
                <div className="w-[28px] h-[28px] shrink-0 rounded-lg overflow-hidden">
                    <ProfilePhoto userId={id} fullName={member.fullName} />
                </div>
                <div className="flex items-center gap-2 grow">
                    <div className="grow shrink min-w-0 flex items-center gap-1">
                        <div
                            className={classNames("shrink min-w-0 truncate", {
                                "text-white": entity.visible,
                                "text-white-400": !entity.visible,
                            })}
                        >
                            {member.fullName}
                        </div>
                        {location && (
                            <Icons.SmallIcon
                                Icon={getBatteryIcon({ percentage: location.batteryPercentage })}
                                className={classNames({
                                    "text-white-300": entity.visible,
                                    "text-white-200": !entity.visible,
                                })}
                            />
                        )}
                        {showLiveBadge && <LiveBadge />}
                    </div>
                    {isHovered || !entity.visible || !location ? (
                        <div className="shrink-0">
                            <LayerListItemActions
                                id={id}
                                isHovered={isHovered}
                                useActions={useActions}
                            />
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5 shrink-0">
                            <small className="text-white-400">{timeRemaining}</small>
                            <TimeRemainingPie expiresAt={location.expiresAt} />
                        </div>
                    )}
                </div>
            </div>
        )
    }
    return Component
}
